/**
 * Map hover popups of the route report (ADR 0018 note) and the spaghetti diagram (ADR 0025 annex): when the
 * pointer moves along a drawn line, find the nearest drawn point and show when the vehicle was there and
 * how fast it went. Pure TypeScript.
 *
 * Lookup: a uniform grid (default 20 m cells, local equirectangular metres) over the line SEGMENTS, built
 * once per data set. A query looks at the cells within the pointer's tolerance (12 px converted to metres
 * at the current zoom), measures the distance to each candidate segment and snaps to the nearer end of the
 * closest one — so the pointer anywhere on the line (not only near a sample) shows the closest sample.
 * When the tolerance covers too many cells (zoomed far out), the segments are scanned linearly instead
 * (60 000 segments ≈ 1 ms). Segments much longer than a cell (rare jumps) are kept in a short list that
 * every query checks. Build: O(n); query: O(candidates).
 */
import { localParts } from "./alarms.js";
import { M_PER_DEG } from "./heatmap.js";

export type HoverPos = { lat: number; lon: number };
/** A hit: which line, which point in it, and the pointer's distance to the line (metres). */
export type HoverHit = { line: number; index: number; distM: number };

export type HoverIndex = {
  readonly cellM: number;
  readonly lat0: number;
  readonly lon0: number;
  readonly kx: number;
  /** Segment ends as local metres: x1, y1, x2, y2 per segment. */
  readonly seg: Float64Array;
  /** Line and index of each segment's first point (the second is index + 1, or the same for a lone point). */
  readonly segLine: Int32Array;
  readonly segIdx: Int32Array;
  readonly segLone: Uint8Array;
  readonly cells: Map<number, number[]>;
  readonly long: number[];
  readonly count: number;
};

/** Grid cells per query above which the segments are scanned linearly. */
const MAX_QUERY_CELLS = 4096;
/** A segment spanning more cells than this (per axis) goes to the always-checked list. */
const LONG_CELLS = 8;
const key = (cx: number, cy: number) => cx * 2_000_003 + cy;

/** Builds the index over lines of points (a line of one point is a single hittable point). */
export function buildHoverIndex(lines: readonly (readonly HoverPos[])[], cellM = 20): HoverIndex {
  let first: HoverPos | undefined;
  let n = 0;
  for (const l of lines) {
    if (!first && l.length) first = l[0];
    n += l.length > 1 ? l.length - 1 : l.length;
  }
  const lat0 = first?.lat ?? 0;
  const lon0 = first?.lon ?? 0;
  const kx = M_PER_DEG * Math.cos((lat0 * Math.PI) / 180);
  const seg = new Float64Array(n * 4);
  const segLine = new Int32Array(n);
  const segIdx = new Int32Array(n);
  const segLone = new Uint8Array(n);
  const cells = new Map<number, number[]>();
  const long: number[] = [];
  let s = 0;
  const add = (li: number, i: number, a: HoverPos, b: HoverPos, lone: boolean) => {
    const x1 = (a.lon - lon0) * kx, y1 = (a.lat - lat0) * M_PER_DEG, x2 = (b.lon - lon0) * kx, y2 = (b.lat - lat0) * M_PER_DEG;
    seg.set([x1, y1, x2, y2], s * 4);
    segLine[s] = li;
    segIdx[s] = i;
    segLone[s] = lone ? 1 : 0;
    const cx1 = Math.floor(Math.min(x1, x2) / cellM), cx2 = Math.floor(Math.max(x1, x2) / cellM);
    const cy1 = Math.floor(Math.min(y1, y2) / cellM), cy2 = Math.floor(Math.max(y1, y2) / cellM);
    if (cx2 - cx1 > LONG_CELLS || cy2 - cy1 > LONG_CELLS || !Number.isFinite(x1 + y1 + x2 + y2)) long.push(s);
    else
      for (let cx = cx1; cx <= cx2; cx++)
        for (let cy = cy1; cy <= cy2; cy++) {
          const k = key(cx, cy);
          const c = cells.get(k);
          if (c) c.push(s);
          else cells.set(k, [s]);
        }
    s++;
  };
  lines.forEach((l, li) => {
    if (l.length === 1) add(li, 0, l[0]!, l[0]!, true);
    for (let i = 0; i + 1 < l.length; i++) add(li, i, l[i]!, l[i + 1]!, false);
  });
  return { cellM, lat0, lon0, kx, seg, segLine, segIdx, segLone, cells, long, count: s };
}

/**
 * The drawn point nearest to the pointer, if the pointer is within `maxM` metres of a line: the nearer end
 * of the closest segment. `skip(line)` leaves lines out (e.g. vehicles hidden in the legend).
 */
export function hoverNearest(ix: HoverIndex, at: HoverPos, maxM: number, skip?: (line: number) => boolean): HoverHit | null {
  if (!ix.count || !(maxM > 0)) return null;
  const px = (at.lon - ix.lon0) * ix.kx;
  const py = (at.lat - ix.lat0) * M_PER_DEG;
  let best = -1, bestD = maxM, bestT = 0;
  const test = (s: number) => {
    if (skip && skip(ix.segLine[s]!)) return;
    const o = s * 4;
    const x1 = ix.seg[o]!, y1 = ix.seg[o + 1]!, dx = ix.seg[o + 2]! - x1, dy = ix.seg[o + 3]! - y1;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / l2)) : 0;
    const d = Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
    if (d < bestD || (d === bestD && best < 0)) {
      best = s;
      bestD = d;
      bestT = t;
    }
  };
  const r = Math.ceil(maxM / ix.cellM);
  if ((2 * r + 1) ** 2 > MAX_QUERY_CELLS) {
    for (let s = 0; s < ix.count; s++) test(s);
  } else {
    const cx = Math.floor(px / ix.cellM), cy = Math.floor(py / ix.cellM);
    const seen = new Set<number>();
    for (let x = cx - r; x <= cx + r; x++)
      for (let y = cy - r; y <= cy + r; y++)
        for (const s of ix.cells.get(key(x, y)) ?? []) if (!seen.has(s)) (seen.add(s), test(s));
    for (const s of ix.long) test(s);
  }
  if (best < 0) return null;
  const lone = ix.segLone[best] === 1;
  return { line: ix.segLine[best]!, index: ix.segIdx[best]! + (lone || bestT < 0.5 ? 0 : 1), distM: bestD };
}

/** Metres between two nearby positions (local equirectangular; the tolerance conversion). */
export function hoverDistanceM(a: HoverPos, b: HoverPos): number {
  const kx = M_PER_DEG * Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  return Math.hypot((b.lon - a.lon) * kx, (b.lat - a.lat) * M_PER_DEG);
}

/** Do the instants [fromMs, toMs) touch more than one local day in `tz`? (Then the popup shows the date.) */
export function spansLocalDays(fromMs: number, toMs: number, tz: string): boolean {
  if (!(toMs > fromMs)) return false;
  return localParts(new Date(fromMs), tz).date !== localParts(new Date(toMs - 1), tz).date;
}

/**
 * The popup's time: "12:15:00" (24 h) in the report time zone, with the local date in front when the
 * window spans two local days ("22.10.2026 12:15:00" / "22/10/2026, 12:15:00").
 */
export function formatHoverTime(ms: number, tz: string, locale: "tr" | "en", withDate: boolean): string {
  return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    ...(withDate ? { day: "2-digit", month: "2-digit", year: "numeric" } : {}),
  }).format(new Date(ms));
}

/** Speed rounded to 0.1 km/h in the locale's decimal style ("6,5" / "6.5"); null without a speed. */
export function formatHoverSpeed(kmh: number | null | undefined, locale: "tr" | "en"): string | null {
  if (kmh === null || kmh === undefined || !Number.isFinite(kmh)) return null;
  return (Math.round(kmh * 10) / 10).toLocaleString(locale === "tr" ? "tr-TR" : "en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
